import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { ArrowRight, Sparkles } from "lucide-react";
import { EliaBrandLogo } from "@/components/brand/EliaBrand";

/**
 * ELIA Ecosystem Hub · new /app landing for members and impersonated views.
 * Presents the 8 ELIA modules organized by life stage / situation / professional context.
 */
export default function EliaHome() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/elia-modules/");
        setModules(data.items || []);
      } catch (err) {
        // silent fallback — hub still renders empty
      } finally { setLoading(false); }
    })();
  }, []);

  const grouped = {
    life_stage: modules.filter((m) => m.stage === "life_stage"),
    life_situation: modules.filter((m) => m.stage === "life_situation"),
    professional_context: modules.filter((m) => m.stage === "professional_context"),
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]">
      {/* Ambient warmth */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[600px] overflow-hidden">
        <div className="absolute top-[-15%] left-[-5%] w-[500px] h-[500px] rounded-full bg-[#DCC29A]/12 blur-3xl" />
        <div className="absolute top-0 right-[-10%] w-[600px] h-[600px] rounded-full bg-[#0B2545]/8 blur-3xl" />
      </div>

      <main className="relative max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-24" data-testid="elia-home">
        {/* Hero */}
        <div className="max-w-3xl">
          <div className="flex items-center gap-3">
            <EliaBrandLogo size={44} />
            <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
              ALTIORA · navigateur économique intelligent
            </div>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1F2937]">
            Prenez de meilleures décisions,<br />
            <span className="text-[#0B2545]">tout au long de votre vie.</span>
          </h1>
          <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[#5B5B62] max-w-2xl">
            ALTIORA est un système d&apos;intelligence — propulsé par ELIA — qui vous accompagne à chaque étape.
            Choisissez le module qui parle à votre situation aujourd&apos;hui — vous pourrez
            toujours en activer d&apos;autres demain.
          </p>
        </div>

        {/* Groups */}
        <div className="mt-16 space-y-16">
          <EliaGroup
            eyebrow="Étape de vie"
            title="Là où vous êtes aujourd'hui."
            modules={grouped.life_stage}
          />
          <EliaGroup
            eyebrow="Situation active"
            title="Ce que vous vivez maintenant."
            modules={grouped.life_situation}
          />
          <EliaGroup
            eyebrow="Contexte professionnel"
            title="Votre rôle, votre organisation."
            modules={grouped.professional_context}
          />
        </div>

        {loading && (
          <div className="mt-16 text-center text-sm text-[#5B5B62] font-mono">Chargement de l&apos;écosystème ALTIORA…</div>
        )}
      </main>
    </div>
  );
}

function EliaGroup({ eyebrow, title, modules }) {
  if (!modules?.length) return null;
  return (
    <section data-testid={`elia-group-${eyebrow.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
        {eyebrow}
      </div>
      <h2 className="mt-2 font-display text-2xl md:text-3xl tracking-tight text-[#1F2937]">{title}</h2>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((m, i) => (
          <EliaModuleCard key={m.id} module={m} delay={i * 0.05} />
        ))}
      </div>
    </section>
  );
}

function EliaModuleCard({ module: m, delay = 0 }) {
  const IconRaw = Lucide[toPascal(m.icon)] || Sparkles;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={m.coming_soon ? "#" : `/elia/${m.slug}`}
        data-testid={`elia-module-card-${m.slug}`}
        className={`group block rounded-xl border border-[#E4E1D8] bg-white p-6 shadow-sm hover:shadow-md transition-all ${
          m.coming_soon ? "opacity-70 cursor-not-allowed" : "hover:border-[#0B2545]/40"
        }`}
        style={{ borderLeft: `4px solid ${m.accent_color}` }}
        onClick={(e) => { if (m.coming_soon) e.preventDefault(); }}
      >
        <div className="flex items-start justify-between gap-4">
          <div
            className="h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${m.accent_color}15`, color: m.accent_color }}
          >
            <IconRaw className="h-5 w-5" strokeWidth={1.5} />
          </div>
          {m.coming_soon && (
            <span className="text-[9px] font-mono uppercase tracking-widest text-[#7A5B14] bg-[#FBF6EA] rounded-full px-2 py-0.5">
              Bientôt
            </span>
          )}
        </div>
        <h3 className="mt-4 font-display text-xl tracking-tight text-[#1F2937]">{m.name_fr}</h3>
        <p className="mt-1 text-[13px] text-[#7A5B14] italic">« {m.tagline_fr} »</p>
        <p className="mt-3 text-sm text-[#5B5B62] leading-relaxed">{m.description_fr}</p>
        <div className="mt-4 text-[11px] font-mono uppercase tracking-widest text-[#8B8B8F]">
          Pour : {m.audience_fr}
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-[#5B5B62]">
          <Sparkles className="h-3 w-3 text-[#7A5B14]" strokeWidth={2} />
          <span>Alimenté par ALTIORA · {m.altiora_modules.length} moteur{m.altiora_modules.length > 1 ? "s" : ""}</span>
        </div>
        {!m.coming_soon && (
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#0B2545] group-hover:translate-x-0.5 transition-transform">
            Ouvrir ce module <ArrowRight className="h-3 w-3" strokeWidth={2} />
          </div>
        )}
      </Link>
    </motion.div>
  );
}

function toPascal(name) {
  if (!name) return "Circle";
  return name.split("-").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join("");
}
